import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Plus, Edit, Trash2, RefreshCcw, CheckCircle, XCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, LayoutGrid, Layers, Users, TrendingUp, FileText, List, LayoutList, Sunrise, Sunset, Moon, Sun } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import CustomTimeInput from '../../shared/components/custom/CustomTimeInput';
import '../../core/styles/global-dropdowns.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import {
  SAUDI_DAY_VALUES,
  SAUDI_WORK_DAYS,
  SHIFT_TYPE_VALUES,
  SAUDI_SHIFT_PRESETS,
  normalizeWorkDay,
  normalizeShiftType,
  matchesShiftType,
  formatPointageDate,
} from './pointageHelpers';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  buildPointageControlsStrip,
  buildPointageImportExportProps,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  mapPointageStatCards,
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
  usePointageEntityReference,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import PointageEntityReferenceField from './components/PointageEntityReferenceField';

const parseHoraireMeta = (description) => {
  if (!description) return { notes: '', team_name: '', effective_date: '' };
  try {
    const parsed = JSON.parse(description);
    if (parsed && typeof parsed === 'object') {
      return {
        notes: parsed.notes || '',
        team_name: parsed.team_name || '',
        effective_date: parsed.effective_date || ''
      };
    }
  } catch {
    // plain text description
  }
  return { notes: description, team_name: '', effective_date: '' };
};

const buildHoraireDescription = ({ team_name, notes, effective_date }) =>
  JSON.stringify({
    team_name: team_name || '',
    notes: notes || '',
    effective_date: effective_date || ''
  });

const parseApplicableDays = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [value];
    } catch {
      return value.includes(',') ? value.split(',').map((d) => d.trim()) : [value];
    }
  }
  return [];
};

const mapHoraireFromApi = (row) => {
  if (!row) return row;
  if (row.schedule_code || row.schedule_name) return row;

  const meta = parseHoraireMeta(row.description);
  return {
    id: row.id,
    schedule_code: row.ref || row.schedule_code || '',
    schedule_name: row.nom || row.schedule_name || '',
    shift_type: normalizeShiftType(row.type_horaire || row.shift_type || ''),
    start_time: row.heure_debut || row.start_time || '',
    end_time: row.heure_fin || row.end_time || '',
    break_duration_minutes: row.duree_pause_minutes ?? row.break_duration_minutes ?? '',
    applicable_days: parseApplicableDays(row.jours_travail ?? row.applicable_days),
    team_name: meta.team_name || row.team_name || '',
    status: row.statut || row.status || 'Actif',
    effective_date: meta.effective_date || row.effective_date || (row.created_at ? String(row.created_at).split('T')[0] : ''),
    notes: meta.notes || row.notes || ''
  };
};

const mapHoraireToApi = (form) => ({
  ref: form.schedule_code,
  nom: form.schedule_name,
  description: buildHoraireDescription(form),
  type_horaire: form.shift_type,
  heure_debut: form.start_time,
  heure_fin: form.end_time,
  duree_pause_minutes: form.break_duration_minutes === '' || form.break_duration_minutes == null
    ? null
    : Number(form.break_duration_minutes),
  jours_travail: JSON.stringify(form.applicable_days || []),
  statut: form.status || 'Actif'
});

const HorairesTravailPage = () => {
  const { t, i18n } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.horairesTravailPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();

  const SHIFT_LABEL_KEYS = {
    [SHIFT_TYPE_VALUES.day]: 'shift_day',
    [SHIFT_TYPE_VALUES.morning]: 'shift_morning',
    [SHIFT_TYPE_VALUES.evening]: 'shift_evening',
    [SHIFT_TYPE_VALUES.night]: 'shift_night',
    [SHIFT_TYPE_VALUES.rotating]: 'shift_rotating',
    [SHIFT_TYPE_VALUES.ramadan]: 'shift_ramadan',
    [SHIFT_TYPE_VALUES.weekend]: 'shift_weekend',
    Matin: 'shift_morning',
    'Après-midi': 'shift_evening',
    Nuit: 'shift_night',
    'Week-end': 'shift_weekend',
    Flexible: 'shift_day',
  };
  const getShiftLabel = (value) => {
    const normalized = normalizeShiftType(value);
    const key = SHIFT_LABEL_KEYS[normalized] || SHIFT_LABEL_KEYS[value];
    return key ? p(key) : value || '-';
  };

  const shiftFormOptions = [
    { value: SHIFT_TYPE_VALUES.day, label: p('shift_day'), isDefault: true },
    { value: SHIFT_TYPE_VALUES.morning, label: p('shift_morning'), isDefault: true },
    { value: SHIFT_TYPE_VALUES.evening, label: p('shift_evening'), isDefault: true },
    { value: SHIFT_TYPE_VALUES.night, label: p('shift_night'), isDefault: true },
    { value: SHIFT_TYPE_VALUES.rotating, label: p('shift_rotating'), isDefault: true },
    { value: SHIFT_TYPE_VALUES.ramadan, label: p('shift_ramadan'), isDefault: true },
    { value: SHIFT_TYPE_VALUES.weekend, label: p('shift_weekend'), isDefault: true },
  ];

  const applyShiftPreset = (shiftType) => {
    const preset = SAUDI_SHIFT_PRESETS[normalizeShiftType(shiftType)];
    if (!preset) return;
    setFormData((prev) => ({
      ...prev,
      shift_type: normalizeShiftType(shiftType),
      start_time: preset.start,
      end_time: preset.end,
      break_duration_minutes: preset.breakMinutes,
    }));
  };

  const DAY_VALUES = SAUDI_DAY_VALUES;
  const DAY_LABEL_KEYS = {
    Sunday: 'day_sunday',
    Monday: 'day_monday',
    Tuesday: 'day_tuesday',
    Wednesday: 'day_wednesday',
    Thursday: 'day_thursday',
    Friday: 'day_friday',
    Saturday: 'day_saturday',
    Lundi: 'day_monday',
    Mardi: 'day_tuesday',
    Mercredi: 'day_wednesday',
    Jeudi: 'day_thursday',
    Vendredi: 'day_friday',
    Samedi: 'day_saturday',
    Dimanche: 'day_sunday',
  };
  const getDayLabel = (day) => {
    const normalized = normalizeWorkDay(day);
    const key = DAY_LABEL_KEYS[normalized] || DAY_LABEL_KEYS[day];
    return key ? p(key) : day;
  };

  const getStatusLabel = (status) => {
    if (status === 'Actif') return p('status_active');
    if (status === 'Inactif') return p('status_inactive');
    return p('em_dash');
  };

  const formatDate = (dateStr) => formatPointageDate(dateStr, '-');

  const [searchTerm, setSearchTerm] = useState('');
  const [horaires, setHoraires] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('general');

  const HORAIRE_FORM_FIELD_COUNT = 11;

  const horaireFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_schedule_info'), icon: Clock, iconClass: 'text-emerald-400' },
      { id: 'details', label: p('section_team_notes'), icon: Users, iconClass: 'text-orange-400' },
    ],
  }, HORAIRE_FORM_FIELD_COUNT), [p]);
  
  const [formData, setFormData] = useState({ 
    schedule_code: '',
    schedule_name: '',
    shift_type: '',
    start_time: '',
    end_time: '',
    break_duration_minutes: '',
    applicable_days: [],
    team_name: '',
    status: '',
    effective_date: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('vue_generale');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const generateScheduleRef = usePointageEntityReference('schedule', 'HOR', horaires, 'schedule_code');

  useEffect(() => { loadHoraires(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadHoraires = async () => {
    try {
      const response = await localApiAdapter.get('/pointage/horaires');
      const rows = response?.data || response || [];
      setHoraires(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []).map(mapHoraireFromApi));
    } catch (error) {
      console.error('Error loading horaires:', error);
      setHoraires([]);
    }
  };

  const stats = {
    total: horaires.length,
    morning: horaires.filter((h) => normalizeShiftType(h.shift_type) === SHIFT_TYPE_VALUES.morning).length,
    evening: horaires.filter((h) => normalizeShiftType(h.shift_type) === SHIFT_TYPE_VALUES.evening).length,
    night: horaires.filter((h) => normalizeShiftType(h.shift_type) === SHIFT_TYPE_VALUES.night).length,
    ramadan: horaires.filter((h) => normalizeShiftType(h.shift_type) === SHIFT_TYPE_VALUES.ramadan).length,
    weekend: horaires.filter((h) => normalizeShiftType(h.shift_type) === SHIFT_TYPE_VALUES.weekend).length,
  };

  const shiftFilterItems = [
    { id: SHIFT_TYPE_VALUES.morning, name: p('shift_morning'), icon: Sunrise, count: stats.morning },
    { id: SHIFT_TYPE_VALUES.evening, name: p('shift_evening'), icon: Sun, count: stats.evening },
    { id: SHIFT_TYPE_VALUES.night, name: p('shift_night'), icon: Moon, count: stats.night },
    { id: SHIFT_TYPE_VALUES.ramadan, name: p('shift_ramadan'), icon: Clock, count: stats.ramadan },
    { id: SHIFT_TYPE_VALUES.weekend, name: p('shift_weekend'), icon: Calendar, count: stats.weekend },
  ];

  const mainTabs = [
    { id: 'vue_generale', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_shift', name: p('tab_by_shift'), icon: Clock },
    { id: 'par_equipe', name: p('tab_by_team'), icon: Users }
  ];

  const quickFilterButtons = [
    { id: 'all', name: t('common.all'), icon: Layers },
    ...shiftFilterItems.map(({ id, name, icon }) => ({ id, name, icon })),
  ];

  const getShiftDistribution = () =>
    shiftFilterItems.map(({ id, name, count }) => ({
      filterKey: id,
      name,
      count,
      color:
        id === SHIFT_TYPE_VALUES.morning ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
        : id === SHIFT_TYPE_VALUES.evening ? 'bg-gradient-to-r from-orange-500 to-orange-600'
        : id === SHIFT_TYPE_VALUES.night ? 'bg-gradient-to-r from-blue-500 to-blue-600'
        : id === SHIFT_TYPE_VALUES.ramadan ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
        : 'bg-gradient-to-r from-purple-500 to-purple-600',
    }));

  const getEquipeDistribution = () => {
    const equipes = {};
    horaires.forEach(h => {
      if (h.team_name) {
        equipes[h.team_name] = (equipes[h.team_name] || 0) + 1;
      }
    });
    const colors = ["bg-gradient-to-r from-green-500 to-green-600", "bg-gradient-to-r from-blue-500 to-blue-600", "bg-gradient-to-r from-purple-500 to-purple-600", "bg-gradient-to-r from-pink-500 to-pink-600"];
    return Object.entries(equipes).map(([name, count], index) => ({
      filterKey: name,
      name,
      count,
      color: colors[index % colors.length]
    }));
  };

  const horaireDistribution = {
    vue_generale: {
      title: p('dist_overview_title'),
      data: getShiftDistribution(),
    },
    par_shift: {
      title: p('dist_by_shift_title'),
      data: getShiftDistribution()
    },
    par_equipe: {
      title: p('dist_by_team_title'),
      data: getEquipeDistribution()
    }
  };

  const distributionInfo = horaireDistribution[mainViewMode] || horaireDistribution.vue_generale;

  const baseFilteredHoraires = useMemo(() => horaires.filter(h => {
    if (statusFilter !== 'all' && h.status !== statusFilter) return false;

    if (dateRange.start || dateRange.end) {
      const effectiveDate = new Date(h.effective_date);

      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (effectiveDate < startOfDay) return false;
      }

      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (effectiveDate > endOfDay) return false;
      }
    }

    if (searchTerm && !h.schedule_code?.toLowerCase().includes(searchTerm.toLowerCase()) && !h.schedule_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !h.team_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (mainViewMode === 'vue_generale') {
      if (quickFilter !== 'all' && !matchesShiftType(h.shift_type, quickFilter)) return false;
    }
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'vue_generale':
        case 'par_shift':
          if (!matchesShiftType(h.shift_type, activeDistributionFilter)) return false;
          break;
        case 'par_equipe':
          if (h.team_name !== activeDistributionFilter) return false;
          break;
      }
    }
    return true;
  }), [horaires, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('code', (h) => h.schedule_code || ''),
    textColumnRule('name', (h) => h.schedule_name || ''),
    textColumnRule('shift_type', (h) => getShiftLabel(h.shift_type)),
    textColumnRule('start_time', (h) => h.start_time || ''),
    textColumnRule('end_time', (h) => h.end_time || ''),
    textColumnRule('days', (h) => String(Array.isArray(h.applicable_days) ? h.applicable_days.length : 0)),
    textColumnRule('team', (h) => h.team_name || ''),
    exactColumnRule('status', (h) => h.status || ''),
  ], [getShiftLabel, getStatusLabel]);

  const filteredHoraires = useMemo(
    () => applyColumnFilters(baseFilteredHoraires, colFilters, columnFilterRules),
    [baseFilteredHoraires, colFilters, columnFilterRules],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const scheduleCode = (formData.schedule_code || '').trim() || generateScheduleRef();
      const normalizedForm = {
        ...formData,
        schedule_code: scheduleCode,
        applicable_days: Array.isArray(formData.applicable_days) ? formData.applicable_days : []
      };
      const submitData = mapHoraireToApi(normalizedForm);
      if (editingRecord) {
        await localApiAdapter.put(`/pointage/horaires/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/pointage/horaires', submitData);
        notifyCreated(showNotify, p('title'));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error')); return;
    } finally {
      resetForm();
      loadHoraires();
    }
  };

  const handleEdit = (record) => {
    const mapped = mapHoraireFromApi(record);
    setEditingRecord(mapped);
    setFormData({
      ...mapped,
      applicable_days: Array.isArray(mapped.applicable_days) ? mapped.applicable_days : []
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/pointage/horaires/${id}`);
        notifyDeleted(showNotify, p('title'));
        loadHoraires();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/pointage/horaires/${id}`, { statut: newStatus });
      loadHoraires();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/pointage/horaires/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        loadHoraires();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      schedule_code: '', schedule_name: '', shift_type: '', start_time: '', end_time: '',
      break_duration_minutes: '', applicable_days: [], team_name: '', status: '', effective_date: '', notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredHoraires);


  const statusOptions = [
    { value: 'Actif', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'Inactif', label: p('status_inactive'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
  ];

  const statusColumnFilterOptions = useMemo(
    () => statusOptions.map(({ value, label }) => ({ value, label })),
    [p],
  );

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style} = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  const daysOfWeek = DAY_VALUES;

  const listStats = mapPointageStatCards([
    { key: 'total', label: p('stat_total'), value: stats.total, icon: Clock },
    { key: 'morning', label: p('stat_morning_teams'), value: stats.morning, icon: Sunrise },
    { key: 'evening', label: p('stat_afternoon_teams'), value: stats.evening, icon: Sun },
    { key: 'night', label: p('stat_night_teams'), value: stats.night, icon: Moon },
  ]);

  const openCreateModal = () => {
    setEditingRecord(null);
    setFormData({
      schedule_code: generateScheduleRef(),
      schedule_name: '',
      shift_type: '',
      start_time: '',
      end_time: '',
      break_duration_minutes: '',
      applicable_days: [],
      team_name: '',
      status: '',
      effective_date: '',
      notes: '',
    });
    setFormTab('general');
    setShowModal(true);
  };

  const importExportProps = buildPointageImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    p,
    t,
  });

  const advancedOptionsContent = (
    <div className="space-y-4 pt-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'vue_generale' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
              <XCircle size={14} />{p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
          {distributionInfo.data.map((item) => (
            <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      applicable_days: prev.applicable_days.includes(day)
        ? prev.applicable_days.filter(d => d !== day)
        : [...prev.applicable_days, day]
    }));
  };

  const POINTAGE_TABLE_COLUMNS = [
    { key: 'code', labelKey: 'col_code' },
    { key: 'name', labelKey: 'col_name' },
    { key: 'shift_type', labelKey: 'col_shift_type' },
    { key: 'start_time', labelKey: 'col_start_time' },
    { key: 'end_time', labelKey: 'col_end_time' },
    { key: 'days', labelKey: 'col_days' },
    { key: 'team', labelKey: 'col_team' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', label: t('common.actions'), required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  return (
    <>
      <FacturationListPageLayout
        className="space-y-3"
        containerClassName="employees-container"
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Clock}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildPointageBulkDeleteFilterAction({
          count: selectedRecords.length,
          onDelete: handleBulkDelete,
          label: `${t('common.delete')} (${selectedRecords.length})`,
          title: t('common.delete', { defaultValue: 'حذف' }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        onCreate={openCreateModal}
        createLabel={p('new_schedule')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={loadHoraires} className="refresh-button" title={t('common.refresh')}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildPointageControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })}
                  />
                  {isColVisible('code') && (
                    <TableColumnHeaderFilter columnKey="code" label={p('col_code')} value={colFilters.code ?? ''} onChange={(v) => setColFilter('code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('name') && (
                    <TableColumnHeaderFilter columnKey="name" label={p('col_name')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('shift_type') && (
                    <TableColumnHeaderFilter columnKey="shift_type" label={p('col_shift_type')} value={colFilters.shift_type ?? ''} onChange={(v) => setColFilter('shift_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('start_time') && (
                    <TableColumnHeaderFilter columnKey="start_time" label={p('col_start_time')} value={colFilters.start_time ?? ''} onChange={(v) => setColFilter('start_time', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('end_time') && (
                    <TableColumnHeaderFilter columnKey="end_time" label={p('col_end_time')} value={colFilters.end_time ?? ''} onChange={(v) => setColFilter('end_time', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('days') && (
                    <TableColumnHeaderFilter columnKey="days" label={p('col_days')} value={colFilters.days ?? ''} onChange={(v) => setColFilter('days', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('team') && (
                    <TableColumnHeaderFilter columnKey="team" label={p('col_team')} value={colFilters.team ?? ''} onChange={(v) => setColFilter('team', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={POINTAGE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredHoraires.length > 0 ? (
                  filteredHoraires.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {isColVisible('code') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.schedule_code}</span></td>
                      )}
                      {isColVisible('name') && (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{record.schedule_name}</span></div></td>
                      )}
                      {isColVisible('shift_type') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{getShiftLabel(record.shift_type)}</span></td>
                      )}
                      {isColVisible('start_time') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.start_time || '-'}</span></td>
                      )}
                      {isColVisible('end_time') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.end_time || '-'}</span></td>
                      )}
                      {isColVisible('days') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{p('days_count', { count: Array.isArray(record.applicable_days) ? record.applicable_days.length : 0 })}</span></td>
                      )}
                      {isColVisible('team') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.team_name || '-'}</span></td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status')}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Clock size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredHoraires.length })}</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredHoraires.length > 0 ? (
              filteredHoraires.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Clock size={20} className="text-emerald-400" />{record.schedule_name}</h3>
                        <div className="flex">{getStatusBadge(record.status)}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_schedule_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_code')}</span> {record.schedule_code}</div>
                            <div><span className="font-medium">{p('label_name')}</span> {record.schedule_name}</div>
                            <div><span className="font-medium">{p('label_shift_type')}</span> {getShiftLabel(record.shift_type)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_time_range')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_start')}</span> {record.start_time || '-'}</div>
                            <div><span className="font-medium">{p('label_end')}</span> {record.end_time || '-'}</div>
                            <div><span className="font-medium">{p('label_break')}</span> {p('break_minutes', { count: record.break_duration_minutes || 0 })}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_applicable_days')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_count')}</span> {p('days_count', { count: Array.isArray(record.applicable_days) ? record.applicable_days.length : 0 })}</div>
                            {Array.isArray(record.applicable_days) && record.applicable_days.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {record.applicable_days.slice(0, 3).map(day => (
                                  <span key={day} className="px-2 py-0.5 bg-white/10 rounded text-xs">{getDayLabel(day)}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_team_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_team')}</span> {record.team_name || '-'}</div>
                            <div><span className="font-medium">{p('label_effective_date')}</span> {formatDate(record.effective_date)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Clock size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {showViewModal && viewingRecord && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="horaires-travail-view-modal"
        >
          <div className="space-y-4">
                <div className={modalForm.viewCard}>
                  <h3 className={`${modalForm.viewHeading} flex items-center gap-2`}><Clock size={18} className="text-emerald-400" />{p('section_schedule_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={modalForm.viewLabel}>{p('col_code')}</p><p className={modalForm.viewValue}>{viewingRecord.schedule_code}</p></div>
                    <div><p className={modalForm.viewLabel}>{t('common.name')}</p><p className={modalForm.viewValue}>{viewingRecord.schedule_name}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_shift_type')}</p><p className={modalForm.viewValue}>{getShiftLabel(viewingRecord.shift_type)}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_start_time')}</p><p className={modalForm.viewValue}>{viewingRecord.start_time || '-'}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_end_time')}</p><p className={modalForm.viewValue}>{viewingRecord.end_time || '-'}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('field_break_minutes')}</p><p className={modalForm.viewValue}>{viewingRecord.break_duration_minutes || 0}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_applicable_days')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('label_days_count')}</p><p className={modalForm.viewValue}>{Array.isArray(viewingRecord.applicable_days) ? viewingRecord.applicable_days.length : 0}</p></div>
                      {Array.isArray(viewingRecord.applicable_days) && viewingRecord.applicable_days.length > 0 && (
                        <div><p className={modalForm.viewLabel}>{p('label_days')}</p><div className="flex flex-wrap gap-2">{viewingRecord.applicable_days.map(day => <span key={day} className="px-3 py-1 bg-white/10 rounded-full text-xs text-white">{getDayLabel(day)}</span>)}</div></div>
                      )}
                    </div>
                  </div>
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_team_notes')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('col_team')}</p><p className={modalForm.viewValue}>{viewingRecord.team_name || '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{t('common.status')}</p>{getStatusBadge(viewingRecord.status)}</div>
                      <div><p className={modalForm.viewLabel}>{p('field_effective_date')}</p><p className={modalForm.viewValue}>{formatDate(viewingRecord.effective_date)}</p></div>
                      {viewingRecord.notes && <div><p className={modalForm.viewLabel}>{p('label_notes')}</p><p className={modalForm.viewValue}>{viewingRecord.notes}</p></div>}
                    </div>
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      )}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit') : p('modal_new')}
          icon={Clock}
          onSubmit
          formId="horaire-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          maxWidth="1200px"
          testId="horaires-travail-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={horaireFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="horaire-form"
            onSubmit={handleSubmit}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
          >
            {pointageFormTabVisible(horaireFormTabs, formTab, 'general') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <PointageEntityReferenceField
                  label={p('field_code')}
                  value={formData.schedule_code}
                  onChange={(v) => setFormData({ ...formData, schedule_code: v })}
                  onRegenerate={editingRecord ? undefined : () => setFormData({ ...formData, schedule_code: generateScheduleRef() })}
                  placeholder={p('ph_code')}
                  autoGeneratedHint={editingRecord ? undefined : p('auto_ref_hint', { defaultValue: 'يُولَّد تلقائياً' })}
                  regenerateTitle={t('common.regenerate', { defaultValue: 'إعادة توليد الرمز' })}
                  inputClassName="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  labelClassName={modalForm.label}
                />
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('field_name')} />}</label><input type="text" required value={formData.schedule_name} onChange={(e) => setFormData({ ...formData, schedule_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_schedule_name')} /></div>
                <div><label className={modalForm.label}>{p('field_shift_type')}</label><CustomSelect value={formData.shift_type} onChange={(value) => applyShiftPreset(value)} options={shiftFormOptions} searchable={true} creatable={true} autoSelect={false} /></div>
                <div><label className={modalForm.label}>{p('field_status')}</label><CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={[
                  { value: 'Actif', label: p('status_active'), icon: <CheckCircle size={14} className="text-green-400" />, isDefault: true },
                  { value: 'Inactif', label: p('status_inactive'), icon: <XCircle size={14} className="text-gray-400" />, isDefault: true }
                ]} searchable={true} creatable={true} autoSelect={false} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_start_time')}</label><CustomTimeInput value={formData.start_time} onChange={(value) => setFormData({ ...formData, start_time: value })} placeholder={p('ph_start_time')} /></div>
                <div><label className={modalForm.label}>{p('field_end_time')}</label><CustomTimeInput value={formData.end_time} onChange={(value) => setFormData({ ...formData, end_time: value })} placeholder={p('ph_end_time')} /></div>
                <div><label className={modalForm.label}>{p('field_break_minutes')}</label><input type="number" value={formData.break_duration_minutes} onChange={(e) => setFormData({ ...formData, break_duration_minutes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_break')} /></div>
              </div>
              </div>
            )}

            {pointageFormTabVisible(horaireFormTabs, formTab, 'details') && (
              <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayToggle(day)}
                    className={`px-4 py-2 rounded-xl font-medium transition-all ${
                      formData.applicable_days.includes(day)
                        ? 'bg-blue-500/30 text-white border-2 border-blue-400'
                        : 'bg-white/5 text-white/60 border border-white/20 hover:bg-white/10'
                    }`}
                  >
                    {getDayLabel(day)}
                  </button>
                ))}
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_team_name')}</label><CustomSelect value={formData.team_name} onChange={(value) => setFormData({ ...formData, team_name: value })} options={[]} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_team')} /></div>
                <div><label className={modalForm.label}>{p('field_effective_date')}</label><CustomDatePicker value={formData.effective_date} onChange={(value) => setFormData({ ...formData, effective_date: value })} placeholder={t('common.select')} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('label_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_notes')} /></div>
              </div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default HorairesTravailPage;

